<template>
  <div class="p-4">
    <div class="license-toolbar d-flex flex-wrap justify-content-between align-items-center mb-4">
      <b-form class="d-flex flex-wrap align-items-center mb-0" @submit.prevent="applySearch">
        <!-- Status filter -->
        <b-dropdown :text="statusLabel" variant="outline-secondary" class="mr-2 mb-2">
          <b-dropdown-item @click="updateFilter('status', null)">All statuses</b-dropdown-item>
          <b-dropdown-item
            v-for="s in statusOptions"
            :key="s"
            @click="updateFilter('status', s)"
          >
            {{ s }}
          </b-dropdown-item>
        </b-dropdown>

        <!-- Marketplace filter -->
        <b-dropdown :text="marketplaceLabel" variant="outline-secondary" class="mr-2 mb-2">
          <b-dropdown-item @click="updateFilter('marketplace', null)">All sources</b-dropdown-item>
          <b-dropdown-item
            v-for="m in marketplaceOptions"
            :key="m"
            @click="updateFilter('marketplace', m)"
          >
            {{ m }}
          </b-dropdown-item>
        </b-dropdown>

        <!-- Search: input + button joined in one control -->
        <b-input-group class="license-search mb-2">
          <b-form-input
            v-model="searchInput"
            placeholder="Search code / external id"
          ></b-form-input>
          <b-input-group-append>
            <b-button type="submit" variant="secondary" title="Search">
              <i class="fa fa-search"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form>

      <b-button variant="success" class="mb-2" @click="openCreateModal">
        <i class="fa fa-plus mr-1"></i> Add License
      </b-button>
    </div>

    <!-- Licenses table -->
    <b-table
      :items="licenses"
      :fields="fields"
      striped
      hover
      responsive
      class="license-table"
      :busy="isLoading"
    >
      <template #cell(status)="data">
        <span :class="statusClass(data.item.status)">{{ data.item.status }}</span>
      </template>

      <template #cell(redeemedBy)="data">
        {{ data.item.redeemedBy && data.item.redeemedBy.email || '-' }}
      </template>

      <template #cell(createdAt)="data">
        {{ formatDate(data.item.createdAt) }}
      </template>

      <template #cell(expiresAt)="data">
        <template v-if="data.item.expiresAt">
          {{ formatDate(data.item.expiresAt) }}
          <span v-if="isExpired(data.item)" class="text-red-600 font-semibold">(expired)</span>
        </template>
        <span v-else class="text-gray-500">Never</span>
      </template>

      <template #cell(redeemedAt)="data">
        {{ formatDate(data.item.redeemedAt) }}
      </template>

      <template #cell(actions)="data">
        <div class="action-buttons">
          <b-button
            variant="info"
            size="sm"
            title="Copy code"
            @click="copyCode(data.item.code)"
          >
            <i class="fa fa-copy"></i>
          </b-button>
          <b-button
            variant="danger"
            size="sm"
            :disabled="isLoading || isTerminal(data.item.status)"
            :title="isTerminal(data.item.status) ? 'Already terminal' : 'Revoke license'"
            @click="handleRevoke(data.item)"
          >
            Revoke
          </b-button>
          <b-button
            variant="outline-danger"
            size="sm"
            :disabled="isLoading"
            title="Delete license record"
            @click="handleDelete(data.item)"
          >
            <i class="fa fa-trash"></i>
          </b-button>
        </div>
      </template>
    </b-table>

    <!-- Pagination -->
    <div class="d-flex justify-content-center mt-4">
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        align="center"
      ></b-pagination>
    </div>

    <!-- Create modal -->
    <b-modal v-model="showCreateModal" title="Add Marketplace License" hide-footer>
      <b-form @submit.prevent="handleCreate">
        <b-form-group label="Code *" label-for="license-code">
          <b-form-input
            id="license-code"
            v-model="newCode"
            placeholder="Enter the redemption code"
            required
          ></b-form-input>
        </b-form-group>
        <b-form-group label="Stripe Product" label-for="license-product">
          <b-form-select
            id="license-product"
            v-model="newPlanKey"
            :options="productOptions"
            :disabled="isLoadingProducts"
          ></b-form-select>
          <small class="text-muted">
            Lifetime Stripe products (<code>metadata.lifetime = true</code>). Each
            option is <em>Product name - product id</em>; the license stores the
            Stripe product id, and redeem grants the product's
            <code>metadata.planKey</code>. Leave as default for AhaSlides Lifetime.
          </small>
        </b-form-group>
        <b-form-group label="Redemption window" label-for="license-expiry-days">
          <b-input-group append="days">
            <b-form-input
              id="license-expiry-days"
              v-model.number="newExpiresInDays"
              type="number"
              :min="EXPIRY.MIN"
              :max="EXPIRY.MAX"
              :disabled="newNoExpiry"
              :placeholder="`Default ${EXPIRY.DEFAULT}`"
            ></b-form-input>
          </b-input-group>
          <b-form-checkbox v-model="newNoExpiry" class="mt-2">
            No expiry (code never times out)
          </b-form-checkbox>
          <small class="text-muted">
            An unredeemed code expires after this many days
            (<code>{{ EXPIRY.MIN }}</code>–<code>{{ EXPIRY.MAX }}</code>, default
            <code>{{ EXPIRY.DEFAULT }}</code>). Once redeemed, the granted Lifetime
            plan never expires.
          </small>
        </b-form-group>
        <b-form-group label="External ID" label-for="license-external-id">
          <b-form-input
            id="license-external-id"
            v-model="newExternalId"
            placeholder="Vendor reference (optional; defaults to code)"
          ></b-form-input>
        </b-form-group>
        <b-form-group label="Note" label-for="license-note">
          <b-form-textarea
            id="license-note"
            v-model="newNote"
            placeholder="Optional note stored in the audit payload"
            rows="2"
          ></b-form-textarea>
        </b-form-group>
        <div class="d-flex justify-content-end">
          <b-button variant="secondary" @click="showCreateModal = false">Cancel</b-button>
          <b-button type="submit" variant="primary" class="ml-2" :disabled="isLoading">Create</b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

// Keep these in sync with general-api services-layer/marketplace/constants.js
// (MARKETPLACE_LICENSE_STATUS / MARKETPLACE). The list API returns `status`
// verbatim; a terminal license can no longer be revoked from the UI.
const STATUS_OPTIONS = ['available', 'redeemed', 'refunded', 'revoked']
const MARKETPLACE_OPTIONS = ['manual', 'appsumo', 'stackcommerce']
const TERMINAL_STATUSES = ['refunded', 'revoked']
// Keep in sync with general-api constants.js REDEMPTION_WINDOW_DAYS. The BE
// re-validates, so this only drives the form's default/min/max hints.
const EXPIRY = { DEFAULT: 60, MIN: 7, MAX: 90 }

export default {
  data() {
    return {
      showCreateModal: false,
      newCode: '',
      newPlanKey: '',
      newExternalId: '',
      newNote: '',
      newExpiresInDays: null,
      newNoExpiry: false,
      isLoadingProducts: false,
      searchInput: '',
      EXPIRY,
      statusOptions: STATUS_OPTIONS,
      marketplaceOptions: MARKETPLACE_OPTIONS,
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'code', label: 'Code' },
        { key: 'marketplace', label: 'Source' },
        { key: 'status', label: 'Status' },
        { key: 'planKey', label: 'Plan' },
        { key: 'redeemedBy', label: 'Redeemed By' },
        { key: 'redeemedAt', label: 'Redeemed At' },
        { key: 'createdAt', label: 'Created At' },
        { key: 'expiresAt', label: 'Expires At' },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
    }
  },
  computed: {
    ...mapState('marketplaceLicense', ['licenses', 'pagination', 'isLoading', 'products']),
    // A blank default (BE falls back to ahaslides-lifetime) plus every lifetime
    // Stripe product from the mirror. The stored value is the Stripe product id;
    // the option text is "<product name> - <product id>".
    productOptions() {
      return [
        { value: '', text: 'Default (ahaslides-lifetime)' },
        ...this.products.map((p) => ({
          value: p.productId,
          text: `${p.name} - ${p.productId}`,
        })),
      ]
    },
    statusLabel() {
      return this.$route.query.status ? `Status: ${this.$route.query.status}` : 'Status: All'
    },
    marketplaceLabel() {
      return this.$route.query.marketplace ? `Source: ${this.$route.query.marketplace}` : 'Source: All'
    },
    currentPage: {
      get() {
        return Number(this.$route.query.page || 1)
      },
      set(val) {
        this.changePage(val)
      },
    },
    perPage() {
      return (this.pagination && this.pagination.limit) || 20
    },
    totalRows() {
      return (this.pagination && this.pagination.total) || 0
    },
  },
  created() {
    this.searchInput = this.$route.query.search || ''
    this.fetchFromQuery()
  },
  watch: {
    '$route.query': 'fetchFromQuery',
  },
  methods: {
    ...mapActions('marketplaceLicense', ['fetchLicenses', 'fetchProducts', 'createLicense', 'revokeLicense', 'deleteLicense']),
    async loadProducts() {
      this.isLoadingProducts = true
      try {
        await this.fetchProducts()
      } catch (err) {
        this.$toasted.show('Failed to load Stripe products', { type: 'error', duration: 3000 })
      } finally {
        this.isLoadingProducts = false
      }
    },
    async fetchFromQuery() {
      const { page = 1, limit = 20, status, marketplace, search } = this.$route.query
      try {
        await this.fetchLicenses({
          page: Number(page),
          limit: Number(limit),
          status: status || undefined,
          marketplace: marketplace || undefined,
          search: search || undefined,
        })
      } catch (err) {
        this.$toasted.show('Failed to load licenses', { type: 'error', duration: 4000 })
      }
    },
    // The just-created license is newest-first, so it only shows on page 1.
    // Jump there (via the route so the watcher refetches) unless already on it.
    refreshToFirstPage() {
      if (Number(this.$route.query.page || 1) !== 1) {
        this.changePage(1)
      } else {
        this.fetchFromQuery()
      }
    },
    updateFilter(key, value) {
      this.$router.push({
        query: {
          ...this.$route.query,
          [key]: value === null ? undefined : value,
          page: 1,
        },
      })
    },
    applySearch() {
      this.$router.push({
        query: {
          ...this.$route.query,
          search: this.searchInput ? this.searchInput.trim() : undefined,
          page: 1,
        },
      })
    },
    changePage(page) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page,
        },
      })
    },
    isTerminal(status) {
      return TERMINAL_STATUSES.includes(status)
    },
    // An available code whose redemption window has already passed. Derived on the
    // fly (no stored 'expired' status) — redeem rejects it server-side regardless.
    isExpired(license) {
      return license.status === 'available'
        && !!license.expiresAt
        && new Date(license.expiresAt).getTime() < Date.now()
    },
    statusClass(status) {
      const map = {
        available: 'text-green-600 font-semibold',
        redeemed: 'text-blue-600 font-semibold',
        refunded: 'text-gray-500 font-semibold',
        revoked: 'text-red-600 font-semibold',
      }
      return map[status] || ''
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString()
    },
    openCreateModal() {
      this.newCode = ''
      this.newPlanKey = ''
      this.newExternalId = ''
      this.newNote = ''
      this.newExpiresInDays = null
      this.newNoExpiry = false
      this.showCreateModal = true
      this.loadProducts()
    },
    async handleCreate() {
      const code = this.newCode && this.newCode.trim()
      if (!code) {
        this.$toasted.show('Code is required', { type: 'error', duration: 3000 })
        return
      }
      try {
        await this.createLicense({
          code,
          planKey: this.newPlanKey ? this.newPlanKey.trim() : undefined,
          externalId: this.newExternalId ? this.newExternalId.trim() : undefined,
          rawPayload: this.newNote ? { note: this.newNote.trim() } : undefined,
          // Send exactly one of noExpiry / expiresInDays; omit both to let the BE
          // apply its default window. The BE re-validates the range. Use an
          // explicit empty check (not truthiness) so a typed 0 is still sent —
          // the BE then rejects it as out-of-range instead of it silently
          // falling back to the default the user never chose.
          ...(this.newNoExpiry
            ? { noExpiry: true }
            : (this.newExpiresInDays != null && this.newExpiresInDays !== ''
              ? { expiresInDays: this.newExpiresInDays }
              : {})),
        })
        this.showCreateModal = false
        this.$toasted.show('License created successfully!', { type: 'success', duration: 3000 })
        this.refreshToFirstPage()
      } catch (err) {
        const message = (err && err.body && err.body.error) || 'Failed to create license'
        this.$toasted.show(message, { type: 'error', duration: 4000 })
      }
    },
    async handleRevoke(license) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Revoke license "${license.code || license.id}"? If it was redeemed, the user will be downgraded to free.`,
        {
          title: 'Confirm Revoke',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Revoke',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          centered: true,
        },
      )
      if (!confirmed) return
      try {
        const result = await this.revokeLicense({ id: license.id })
        const note = result && result.downgraded ? ' (user downgraded)' : ''
        this.$toasted.show(`License revoked${note}`, { type: 'success', duration: 3000 })
        this.fetchFromQuery()
      } catch (err) {
        const message = (err && err.body && err.body.error) || 'Failed to revoke license'
        this.$toasted.show(message, { type: 'error', duration: 4000 })
      }
    },
    async handleDelete(license) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Permanently delete license "${license.code || license.id}"? This removes the record only — it does NOT change the user's plan (use Revoke for that). This cannot be undone.`,
        {
          title: 'Confirm Delete',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          centered: true,
        },
      )
      if (!confirmed) return
      try {
        await this.deleteLicense({ id: license.id })
        this.$toasted.show('License deleted', { type: 'success', duration: 3000 })
        this.fetchFromQuery()
      } catch (err) {
        const message = (err && err.body && err.body.error) || 'Failed to delete license'
        this.$toasted.show(message, { type: 'error', duration: 4000 })
      }
    },
    async copyCode(code) {
      if (!code) {
        this.$toasted.show('No code to copy', { type: 'warning', duration: 2000 })
        return
      }
      try {
        await navigator.clipboard.writeText(code)
        this.$toasted.show('Code copied!', { type: 'success', duration: 2000 })
      } catch (err) {
        console.error('Failed to copy code:', err)
        this.$toasted.show('Failed to copy code', { type: 'error', duration: 3000 })
      }
    },
  },
}
</script>

<style scoped>
/* Filter bar: keep the search field a comfortable width and let it sit
   on the same baseline as the dropdowns without collapsing on wrap. */
.license-search {
  width: 280px;
  max-width: 100%;
}

.license-table {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  white-space: nowrap;
}
</style>
